import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	InputAdornment,
	IconButton,
	CircularProgress,
	Tabs,
	Tab,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Card,
	CardContent,
	Grid,
	Divider,
} from "@mui/material";
import AppLayout from "../../components/AppLayout";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClassIcon from "@mui/icons-material/Class";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import { EntradaProdutoService } from "../../service/entrada_produto_service";
import ModalClassificarQualidade from "../../components/ModalClassificarQualidade";
import ModalEditarEntrada from "../../components/ModalEditarEntrada";
import ModalHistoricoEntrada from "../../components/ModalHistoricoEntrada";
import { hojeISO } from "../../utils/dateUtils";

function EntradaProdutos() {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState(0);
	const [recepcao, setRecepcao] = useState([]);
	const [consolidado, setConsolidado] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [total, setTotal] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [dataSelecionada, setDataSelecionada] = useState(hojeISO());
	const [openQualidadeModal, setOpenQualidadeModal] = useState(false);
	const [entradaSelecionada, setEntradaSelecionada] = useState(null);
	const [openDetalhesModal, setOpenDetalhesModal] = useState(false);
	const [consolidadoSelecionado, setConsolidadoSelecionado] = useState(null);
	const [entradaDoHistorico, setEntradaDoHistorico] = useState(null);
	const [entradaEmEdicaoId, setEntradaEmEdicaoId] = useState(null);
	const [entradaParaExcluir, setEntradaParaExcluir] = useState(null);
	const [excluindo, setExcluindo] = useState(false);

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");
	const entradaProdutoService = useMemo(() => new EntradaProdutoService(), []);

	const loadRecepcao = useCallback(async () => {
		setLoading(true);
		try {
			const result = await entradaProdutoService.getRecepcao(
				unidadeDistribuidoraId,
				dataSelecionada,
				page + 1,
				rowsPerPage,
				searchTerm
			);
			setRecepcao(result.data || []);
			setTotal(result.total || 0);
		} catch (error) {
			console.error("Erro ao carregar recepção:", error);
			alert(error.message || "Erro ao carregar recepção");
		} finally {
			setLoading(false);
		}
	}, [entradaProdutoService, unidadeDistribuidoraId, dataSelecionada, page, rowsPerPage, searchTerm]);

	const loadConsolidado = useCallback(async () => {
		setLoading(true);
		try {
			const result = await entradaProdutoService.getConsolidado(
				unidadeDistribuidoraId,
				dataSelecionada,
				page + 1,
				rowsPerPage,
				searchTerm
			);
			const lotes = result.data || [];
			setConsolidado(lotes);
			setTotal(result.total || 0);
			return lotes;
		} catch (error) {
			console.error("Erro ao carregar consolidado:", error);
			alert(error.message || "Erro ao carregar consolidado");
			return [];
		} finally {
			setLoading(false);
		}
	}, [entradaProdutoService, unidadeDistribuidoraId, dataSelecionada, page, rowsPerPage, searchTerm]);

	useEffect(() => {
		if (unidadeDistribuidoraId) {
			if (activeTab === 0) {
				loadRecepcao();
			} else {
				loadConsolidado();
			}
		}
	}, [unidadeDistribuidoraId, activeTab, loadRecepcao, loadConsolidado]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setPage(0);
		}, 500);
		return () => clearTimeout(timeoutId);
	}, [searchTerm]);

	const handleNovaCadastro = () => {
		navigate("/compras/cadastro");
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
		setPage(0);
	};

	const handleDataChange = (event) => {
		setDataSelecionada(event.target.value);
		setPage(0);
	};

	const handleConfirmar = async (id) => {
		try {
			await entradaProdutoService.confirmarEntrada(id);
			loadRecepcao();
		} catch (error) {
			alert(error.message || "Erro ao confirmar entrada");
		}
	};

	const handleNegar = async (id) => {
		try {
			await entradaProdutoService.negarEntrada(id);
			loadRecepcao();
		} catch (error) {
			alert(error.message || "Erro ao negar entrada");
		}
	};

	const handleClassificarQualidade = (entrada) => {
		setEntradaSelecionada(entrada);
		setOpenQualidadeModal(true);
	};

	const handleQualidadeSalva = () => {
		loadRecepcao();
	};

	const handleVerDetalhes = (item) => {
		setConsolidadoSelecionado(item);
		setOpenDetalhesModal(true);
	};

	// Depois de editar/excluir uma entrada o lote é recalculado no servidor:
	// recarrega a lista e reaponta o modal de detalhes para o lote atualizado.
	const recarregarConsolidadoSelecionado = useCallback(async () => {
		const lotes = await loadConsolidado();
		const atualizado = lotes.find((lote) => lote.id === consolidadoSelecionado?.id);

		if (atualizado) {
			setConsolidadoSelecionado(atualizado);
			return;
		}

		// Sem entradas restantes o lote sai do consolidado; não há o que detalhar.
		setOpenDetalhesModal(false);
		setConsolidadoSelecionado(null);
	}, [loadConsolidado, consolidadoSelecionado]);

	const handleEditarEntrada = (entrada) => {
		setEntradaEmEdicaoId(entrada.id);
	};

	const handleExcluirEntrada = async () => {
		if (!entradaParaExcluir) return;

		setExcluindo(true);
		try {
			await entradaProdutoService.remover(entradaParaExcluir.id);
			setEntradaParaExcluir(null);
			await recarregarConsolidadoSelecionado();
		} catch (error) {
			console.error("Erro ao excluir entrada:", error);
			alert(error.message || "Erro ao excluir entrada");
		} finally {
			setExcluindo(false);
		}
	};

	const getStatusChip = (status) => {
		const statusMap = {
			EM_ANALISE: { label: "Em Análise", color: "warning" },
			CONFIRMADO: { label: "Confirmado", color: "success" },
			NEGADO: { label: "Negado", color: "error" },
		};
		const config = statusMap[status] || { label: status, color: "default" };
		return <Chip label={config.label} color={config.color} size="small" />;
	};

	const formatCurrency = (value) => {
		return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
	};

	// `quantidadeOriginal` só existe na entrada nascida de pedido do Sisagri
	// Campo: é o que o produtor declarou, e fica na coluna "Quantidade". O que
	// o distribuidor realmente recebeu (valor da última edição) vai para a
	// coluna extra, que só faz sentido se houver alguma entrada dessa origem.
	const mostrarQuantidadeRecebida = useMemo(
		() => recepcao.some((entrada) => entrada.quantidadeOriginal !== null && entrada.quantidadeOriginal !== undefined),
		[recepcao]
	);
	const colunasRecepcao = mostrarQuantidadeRecebida ? 15 : 14;

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		// Extrair apenas a parte da data (YYYY-MM-DD) e formatar sem conversão de timezone
		const datePart = dateString.split('T')[0];
		const [year, month, day] = datePart.split('-');
		if (!year || !month || !day) return '-';
		return `${day}/${month}/${year}`;
	};

	return (
		<AppLayout currentPage="compras">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com busca e botão */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 3,
						}}
					>
						<Typography
							variant="h4"
							sx={{
								color: "#666",
								fontWeight: 600,
							}}
						>
							Compras
						</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<TextField
								label="Data"
								type="date"
								value={dataSelecionada}
								onChange={handleDataChange}
								InputLabelProps={{ shrink: true }}
								size="small"
								sx={{
									width: "180px",
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
								}}
							/>
							<TextField
								size="small"
								placeholder="Buscar..."
								value={searchTerm}
								onChange={handleSearchChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{
									width: "300px",
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
								}}
							/>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleNovaCadastro}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
									},
									textTransform: "none",
									px: 3,
									py: 1,
									borderRadius: "8px",
								}}
							>
								Nova Entrada
							</Button>
						</Box>
					</Box>

					{/* Tabela com Abas */}
					<Paper
						sx={{
							bgcolor: "white",
							borderRadius: "12px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
							overflow: "hidden",
						}}
					>
						<Tabs
							value={activeTab}
							onChange={handleTabChange}
							sx={{
								borderBottom: 1,
								borderColor: "divider",
								px: 2,
							}}
						>
							<Tab label="Recepção" />
							<Tab label="Consolidado" />
						</Tabs>

						{activeTab === 0 && (
							<>
								<TableContainer>
									<Table>
										<TableHead sx={{ bgcolor: "#f5f5f5" }}>
											<TableRow>
												<TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Produto</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Qualidade</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Produtor</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>NF/Recibo</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Nº Pedido</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Código Rastreio</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Quantidade</TableCell>
												{mostrarQuantidadeRecebida && (
													<TableCell sx={{ fontWeight: 600 }}>Quantidade Recebida</TableCell>
												)}
												<TableCell sx={{ fontWeight: 600 }}>Nº Caixas</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Preço</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
												<TableCell align="center" sx={{ fontWeight: 600 }}>Histórico</TableCell>
												<TableCell align="center" sx={{ fontWeight: 600 }}>Classificar</TableCell>
												<TableCell align="center" sx={{ fontWeight: 600 }}>Confirmar Entrada?</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{loading ? (
												<TableRow>
													<TableCell colSpan={colunasRecepcao} align="center" sx={{ py: 8 }}>
														<CircularProgress sx={{ color: "#1a9e8f" }} />
													</TableCell>
												</TableRow>
											) : recepcao.length === 0 ? (
												<TableRow>
													<TableCell colSpan={colunasRecepcao} align="center" sx={{ py: 8, color: "#999" }}>
														Nenhuma entrada encontrada para esta data
													</TableCell>
												</TableRow>
											) : (
												recepcao.map((entrada) => (
													<TableRow key={entrada.id} hover>
														<TableCell>{formatDate(entrada.data)}</TableCell>
														<TableCell>
															{entrada.produtoUnidadeDistribuidora?.cultivo?.cultura || '-'}
															{entrada.produtoUnidadeDistribuidora?.cultivo?.variedade &&
																` - ${entrada.produtoUnidadeDistribuidora.cultivo.variedade}`}
														</TableCell>
														<TableCell>
															{entrada.qualidades?.length > 0 ? (
																<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
																	{entrada.qualidades.map((q, idx) => (
																		<Typography key={idx} variant="body2" sx={{ fontSize: '14px' }}>
																			{q.qualidadeColheita?.nome || 'Sem qualidade'}: {q.quantidade}
																		</Typography>
																	))}
																	{(() => {
																		const totalClassificado = entrada.qualidades.reduce((sum, q) => sum + q.quantidade, 0);
																		const naoClassificado = entrada.quantidade - totalClassificado;
																		return naoClassificado > 0 ? (
																			<Typography variant="body2" sx={{ fontSize: '14px', color: '#999' }}>
																				Não classificado: {naoClassificado}
																			</Typography>
																		) : null;
																	})()}
																</Box>
															) : '-'}
														</TableCell>
														<TableCell>{entrada.fornecedor?.nome || '-'}</TableCell>
														<TableCell>{entrada.notaFiscalRecibo || '-'}</TableCell>
														<TableCell>{entrada.numeroPedido || '-'}</TableCell>
														<TableCell>{entrada.codigoRastreio || '-'}</TableCell>
														<TableCell>
															<strong>{entrada.quantidadeOriginal ?? entrada.quantidade}</strong>
														</TableCell>
														{mostrarQuantidadeRecebida && (
															<TableCell>
																<strong>{entrada.quantidade}</strong>
															</TableCell>
														)}
														<TableCell>{entrada.numeroCaixas || '-'}</TableCell>
														<TableCell>{formatCurrency(entrada.preco)}</TableCell>
														<TableCell>{getStatusChip(entrada.status)}</TableCell>
														<TableCell align="center">
															{/* Só quem já sofreu alguma alteração tem o que mostrar */}
															{entrada._count?.historico > 0 && (
																<IconButton
																	size="small"
																	onClick={() => setEntradaDoHistorico(entrada)}
																	title={`Ver alterações (${entrada._count.historico})`}
																	sx={{
																		color: "#1a9e8f",
																		"&:hover": { bgcolor: "#e0f7fa" },
																	}}
																>
																	<HistoryIcon />
																</IconButton>
															)}
														</TableCell>
														<TableCell align="center">
															<IconButton
																size="small"
																onClick={() => handleClassificarQualidade(entrada)}
																sx={{ color: "#1a9e8f" }}
															>
																<ClassIcon />
															</IconButton>
														</TableCell>
														<TableCell align="center">
															<Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
																<IconButton
																	size="small"
																	onClick={() => handleConfirmar(entrada.id)}
																	disabled={entrada.status === 'CONFIRMADO'}
																	sx={{
																		color: entrada.status === 'CONFIRMADO' ? '#ccc' : '#4caf50',
																		"&:hover": { bgcolor: "#e8f5e9" }
																	}}
																>
																	<CheckCircleIcon />
																</IconButton>
																<IconButton
																	size="small"
																	onClick={() => handleNegar(entrada.id)}
																	disabled={entrada.status === 'NEGADO'}
																	sx={{
																		color: entrada.status === 'NEGADO' ? '#ccc' : '#f44336',
																		"&:hover": { bgcolor: "#ffebee" }
																	}}
																>
																	<CancelIcon />
																</IconButton>
															</Box>
														</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									rowsPerPageOptions={[5, 10, 25]}
									component="div"
									count={total}
									rowsPerPage={rowsPerPage}
									page={page}
									onPageChange={handleChangePage}
									onRowsPerPageChange={handleChangeRowsPerPage}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
								/>
							</>
						)}

						{activeTab === 1 && (
							<>
								<TableContainer>
									<Table>
										<TableHead sx={{ bgcolor: "#f5f5f5" }}>
											<TableRow>
												<TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Nº do Lote</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Produto</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Qualidade</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Quantidade</TableCell>
												<TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{loading ? (
												<TableRow>
													<TableCell colSpan={7} align="center" sx={{ py: 8 }}>
														<CircularProgress sx={{ color: "#1a9e8f" }} />
													</TableCell>
												</TableRow>
											) : consolidado.length === 0 ? (
												<TableRow>
													<TableCell colSpan={7} align="center" sx={{ py: 8, color: "#999" }}>
														Nenhum item consolidado encontrado para esta data
													</TableCell>
												</TableRow>
											) : (
												consolidado.map((item) => (
													<TableRow key={item.id} hover>
														<TableCell>{dataSelecionada && formatDate(dataSelecionada)}</TableCell>
														<TableCell>{item.codigoRastreio || '-'}</TableCell>
														<TableCell>
															{item.produto?.cultivo?.cultura || '-'}
															{item.produto?.cultivo?.variedade &&
																` - ${item.produto.cultivo.variedade}`}
														</TableCell>
														<TableCell>{item.unidadeMedida?.sigla || '-'}</TableCell>
														<TableCell>{item.qualidadeColheita?.nome || 'Sem qualidade'}</TableCell>
														<TableCell>{item.quantidade}</TableCell>
														<TableCell align="center">
															<IconButton
																size="small"
																onClick={() => handleVerDetalhes(item)}
																sx={{
																	color: "#1a9e8f",
																	"&:hover": { bgcolor: "#e0f7fa" }
																}}
															>
																<VisibilityIcon />
															</IconButton>
														</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									rowsPerPageOptions={[5, 10, 25]}
									component="div"
									count={total}
									rowsPerPage={rowsPerPage}
									page={page}
									onPageChange={handleChangePage}
									onRowsPerPageChange={handleChangeRowsPerPage}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
								/>
							</>
						)}
					</Paper>
			</Box>

			{/* Modal de Classificação de Qualidade */}
			<ModalClassificarQualidade
				open={openQualidadeModal}
				onClose={() => setOpenQualidadeModal(false)}
				entrada={entradaSelecionada}
				onSave={handleQualidadeSalva}
			/>

			{/* Modal de Detalhes do Consolidado */}
			<Dialog
				open={openDetalhesModal}
				onClose={() => setOpenDetalhesModal(false)}
				maxWidth="lg"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: "12px",
					},
				}}
			>
				<DialogTitle
					sx={{
						bgcolor: "#1a9e8f",
						color: "white",
						fontWeight: 600,
						fontSize: "1.5rem",
					}}
				>
					Detalhes do Consolidado
				</DialogTitle>
				<DialogContent sx={{ mt: 2, px: 3 }}>
					{consolidadoSelecionado && (
						<>
							{/* Card com resumo do consolidado */}
							<Card
								sx={{
									mb: 3,
									borderRadius: "8px",
									boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
									bgcolor: "#f8f9fa",
								}}
							>
								<CardContent>
									<Grid container spacing={2}>
										<Grid size={{ xs: 12 }}>
											<Typography
												variant="h5"
												sx={{
													color: "#333",
													fontWeight: 600,
													mb: 2,
												}}
											>
												{consolidadoSelecionado.produto?.cultivo?.cultura}
												{consolidadoSelecionado.produto?.cultivo?.variedade &&
													` - ${consolidadoSelecionado.produto.cultivo.variedade}`}
											</Typography>
										</Grid>
										<Grid size={{ xs: 12, sm: 4 }}>
											<Box>
												<Typography
													variant="caption"
													sx={{
														color: "#666",
														textTransform: "uppercase",
														fontWeight: 600,
													}}
												>
													Nº do Lote
												</Typography>
												<Typography variant="h6" sx={{ color: "#1a9e8f", fontWeight: 500 }}>
													{consolidadoSelecionado.codigoRastreio || '-'}
												</Typography>
											</Box>
										</Grid>
										<Grid size={{ xs: 12, sm: 4 }}>
											<Box>
												<Typography
													variant="caption"
													sx={{
														color: "#666",
														textTransform: "uppercase",
														fontWeight: 600,
													}}
												>
													Qualidade
												</Typography>
												<Typography variant="h6" sx={{ color: "#1a9e8f", fontWeight: 500 }}>
													{consolidadoSelecionado.qualidadeColheita?.nome || 'Sem qualidade'}
												</Typography>
											</Box>
										</Grid>
										<Grid size={{ xs: 12, sm: 4 }}>
											<Box>
												<Typography
													variant="caption"
													sx={{
														color: "#666",
														textTransform: "uppercase",
														fontWeight: 600,
													}}
												>
													Quantidade Total
												</Typography>
												<Typography variant="h6" sx={{ color: "#1a9e8f", fontWeight: 500 }}>
													{consolidadoSelecionado.quantidade}
												</Typography>
											</Box>
										</Grid>
									</Grid>
								</CardContent>
							</Card>

							<Divider sx={{ mb: 3 }} />

							{/* Lista de entradas */}
							<Typography
								variant="h6"
								sx={{
									color: "#333",
									fontWeight: 600,
									mb: 2,
								}}
							>
								Entradas que compõem este consolidado ({consolidadoSelecionado.entradas?.length || 0})
							</Typography>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								{consolidadoSelecionado.entradas?.map((entrada) => (
									<Card
										key={entrada.id}
										sx={{
											borderRadius: "8px",
											boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
											transition: "box-shadow 0.3s",
											"&:hover": {
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
											},
										}}
									>
										<CardContent>
											<Grid container spacing={2}>
												<Grid size={{ xs: 12 }}>
													<Box
														sx={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
															gap: 1,
															mb: 1,
														}}
													>
														<Typography
															variant="h6"
															sx={{
																color: "#333",
																fontWeight: 600,
															}}
														>
															{entrada.fornecedor?.nome || 'Fornecedor não informado'}
														</Typography>
														<Box sx={{ display: "flex", gap: 0.5 }}>
															<IconButton
																size="small"
																onClick={() => handleEditarEntrada(entrada)}
																title="Editar entrada"
																sx={{
																	color: "#1a9e8f",
																	"&:hover": { bgcolor: "#e0f7fa" },
																}}
															>
																<EditIcon fontSize="small" />
															</IconButton>
															<IconButton
																size="small"
																onClick={() => setEntradaParaExcluir(entrada)}
																title="Excluir entrada"
																sx={{
																	color: "#f44336",
																	"&:hover": { bgcolor: "#ffebee" },
																}}
															>
																<DeleteIcon fontSize="small" />
															</IconButton>
														</Box>
													</Box>
												</Grid>
												<Grid size={{ xs: 12, sm: 6, md: 3 }}>
													<Typography
														variant="caption"
														sx={{
															color: "#666",
															display: "block",
															textTransform: "uppercase",
															fontWeight: 600,
														}}
													>
														Data
													</Typography>
													<Typography variant="body1" sx={{ color: "#333" }}>
														{formatDate(entrada.data)}
													</Typography>
												</Grid>
												<Grid size={{ xs: 12, sm: 6, md: 3 }}>
													<Typography
														variant="caption"
														sx={{
															color: "#666",
															display: "block",
															textTransform: "uppercase",
															fontWeight: 600,
														}}
													>
														Quantidade
													</Typography>
													<Typography variant="body1" sx={{ color: "#333", fontWeight: 500 }}>
														{entrada.quantidade}
													</Typography>
												</Grid>
												<Grid size={{ xs: 12, sm: 6, md: 3 }}>
													<Typography
														variant="caption"
														sx={{
															color: "#666",
															display: "block",
															textTransform: "uppercase",
															fontWeight: 600,
														}}
													>
														Preço
													</Typography>
													<Typography
														variant="body1"
														sx={{
															color: "#1a9e8f",
															fontWeight: 600,
														}}
													>
														{formatCurrency(entrada.preco)}
													</Typography>
												</Grid>
												<Grid size={{ xs: 12, sm: 6, md: 3 }}>
													<Typography
														variant="caption"
														sx={{
															color: "#666",
															display: "block",
															textTransform: "uppercase",
															fontWeight: 600,
														}}
													>
														Nº Caixas
													</Typography>
													<Typography variant="body1" sx={{ color: "#333" }}>
														{entrada.numeroCaixas || '-'}
													</Typography>
												</Grid>
												{entrada.notaFiscalRecibo && (
													<Grid size={{ xs: 12, sm: 6 }}>
														<Typography
															variant="caption"
															sx={{
																color: "#666",
																display: "block",
																textTransform: "uppercase",
																fontWeight: 600,
															}}
														>
															NF/Recibo
														</Typography>
														<Typography variant="body1" sx={{ color: "#333" }}>
															{entrada.notaFiscalRecibo}
														</Typography>
													</Grid>
												)}
												{entrada.numeroPedido && (
													<Grid size={{ xs: 12, sm: 6 }}>
														<Typography
															variant="caption"
															sx={{
																color: "#666",
																display: "block",
																textTransform: "uppercase",
																fontWeight: 600,
															}}
														>
															Nº Pedido
														</Typography>
														<Typography variant="body1" sx={{ color: "#333" }}>
															{entrada.numeroPedido}
														</Typography>
													</Grid>
												)}
												{entrada.codigoRastreio && (
													<Grid size={{ xs: 12, sm: 6 }}>
														<Typography
															variant="caption"
															sx={{
																color: "#666",
																display: "block",
																textTransform: "uppercase",
																fontWeight: 600,
															}}
														>
															Código de Rastreio
														</Typography>
														<Typography variant="body1" sx={{ color: "#333" }}>
															{entrada.codigoRastreio}
														</Typography>
													</Grid>
												)}
											</Grid>
										</CardContent>
									</Card>
								))}
							</Box>
						</>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						onClick={() => setOpenDetalhesModal(false)}
						variant="contained"
						sx={{
							bgcolor: "#1a9e8f",
							"&:hover": {
								bgcolor: "#138a7d",
							},
							textTransform: "none",
							px: 3,
							borderRadius: "8px",
						}}
					>
						Fechar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Trilha de alterações de uma entrada da recepção */}
			<ModalHistoricoEntrada
				open={!!entradaDoHistorico}
				entrada={entradaDoHistorico}
				onClose={() => setEntradaDoHistorico(null)}
			/>

			{/* Edição de uma entrada do lote consolidado */}
			<ModalEditarEntrada
				open={!!entradaEmEdicaoId}
				entradaId={entradaEmEdicaoId}
				onClose={() => setEntradaEmEdicaoId(null)}
				onSave={recarregarConsolidadoSelecionado}
			/>

			{/* Confirmação de exclusão de uma entrada do lote consolidado */}
			<Dialog
				open={!!entradaParaExcluir}
				onClose={excluindo ? undefined : () => setEntradaParaExcluir(null)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle sx={{ fontWeight: 600 }}>Excluir entrada</DialogTitle>
				<DialogContent>
					<Typography variant="body1" sx={{ color: "#555" }}>
						Excluir a entrada de{" "}
						<strong>{entradaParaExcluir?.fornecedor?.nome || "fornecedor não informado"}</strong>{" "}
						({entradaParaExcluir?.quantidade}) deste lote? A quantidade do consolidado será
						recalculada.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						onClick={() => setEntradaParaExcluir(null)}
						disabled={excluindo}
						sx={{ textTransform: "none", color: "#666" }}
					>
						Cancelar
					</Button>
					<Button
						onClick={handleExcluirEntrada}
						variant="contained"
						color="error"
						disabled={excluindo}
						sx={{ textTransform: "none", px: 3 }}
					>
						{excluindo ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Excluir"}
					</Button>
				</DialogActions>
			</Dialog>
		</AppLayout>
	);
}

export default EntradaProdutos;
